import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography, Avatar } from "@mui/material";
import { InfoOutlined as AboutIcon } from "@mui/icons-material";
import {
  ExpandMore as ChevronDownIcon,
  ExpandLess as ChevronUpIcon,
  Gavel as RulesIcon,
  SupervisorAccount as AdminIcon,
  LocalFireDepartment as HotIcon,
  TrendingUp as TrendingIcon,
  FiberNew as NewIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useTheme } from "../components/ThemeContext";
import CommunityLeftSidebar from "../components/CommunityLeftSidebar";
import PostCommunity from "../components/PostCommunity";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileNavDrawer from "../components/MobileNavDrawer";
import MobileWidgetDrawer from "../components/MobileWidgetDrawer";
import axios from "axios";
import { PageContext } from "../context/PageContext";
import { throttle } from "lodash";
import { DeviceContext } from "../context/DeviceContext";
import { AuthContext } from "../context/AuthContext";
import Utils from "../utils/Utils";
import { getImageUrl } from "../utils/imageUrl";

const TOPBAR_HEIGHT = 64;
const SIDEBAR_LEFT_WIDTH = 264;
const SIDEBAR_RIGHT_WIDTH = 320;

// ── Filter tab button ─────────────────────────────────────────────────────────
function FilterTab({ label, icon: Icon, active, onClick }) {
  const { theme } = useTheme();
  return (
    <Button
      onClick={onClick}
      startIcon={<Icon sx={{ fontSize: "14px !important" }} />}
      sx={{
        borderRadius: "8px",
        px: 2,
        py: 0.8,
        fontWeight: 600,
        fontSize: "0.875rem",
        textTransform: "none",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease",
        ...(active
          ? {
              background: `linear-gradient(159.695deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
              color: "#fff",
              boxShadow: "0 2px 8px rgba(98,244,129,0.4)",
              "&:hover": { filter: "brightness(110%)" },
            }
          : {
              backgroundColor: "transparent",
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.palette.border.subtle}`,
              "&:hover": {
                borderColor: theme.palette.border.green,
                color: theme.palette.text.primary,
              },
            }),
      }}
    >
      {label}
    </Button>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────
function CollapsibleSection({ icon: Icon, title, children, defaultOpen = false }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box
      sx={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px",
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { background: theme.palette.background.highlight },
          transition: "background 0.2s ease",
        }}
      >
        {Icon && <Icon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
        <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.875rem", color: theme.palette.text.primary }}>
          {title}
        </Typography>
        {open ? (
          <ChevronUpIcon sx={{ fontSize: 18, color: theme.palette.text.muted }} />
        ) : (
          <ChevronDownIcon sx={{ fontSize: 18, color: theme.palette.text.muted }} />
        )}
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

// ── Community right-panel (info sidebar) ─────────────────────────────────────
function CommunityInfoPanel({ communityInfo, bannerUrl, iconUrl, isFollowing, onFollowToggle, isMobile, navigate }) {
  const { theme } = useTheme();
  if (!communityInfo) return null;

  const rulesLines = communityInfo.rules
    ? communityInfo.rules.split("\n").filter((l) => l.trim())
    : [];

  return (
    <Box sx={{ p: 2 }}>
      {/* Banner + Icon */}
      <Box
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          mb: 2,
          boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.1)",
        }}
      >
        {/* Banner */}
        <Box
          sx={{
            height: 96,
            position: "relative",
            background: bannerUrl
              ? `url(${bannerUrl}) center/cover no-repeat`
              : `linear-gradient(135deg, rgba(16,56,32,0.4) 0%, rgba(6,30,20,0.4) 100%)`,
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 60%)`,
            },
          }}
        />

        {/* Icon + name + follow */}
        <Box sx={{ px: 2, pb: 2, mt: "-28px", position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 1.5 }}>
            <Avatar
              alt={communityInfo.name}
              src={iconUrl || undefined}
              sx={{
                width: 64,
                height: 64,
                borderRadius: "12px",
                border: `4px solid ${theme.palette.background.default}`,
                boxShadow: "0px 4px 12px 0px rgba(98,244,129,0.3)",
                background: "linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)",
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
              {...(!iconUrl && Utils.stringAvatar(communityInfo.name))}
            />
            <Button
              onClick={onFollowToggle}
              sx={{
                background: isFollowing
                  ? "transparent"
                  : "linear-gradient(to right, #10b981, #059669)",
                border: isFollowing ? `1px solid rgba(255,255,255,0.15)` : "none",
                color: isFollowing ? theme.palette.text.secondary : "#fff",
                borderRadius: "8px",
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                boxShadow: isFollowing ? "none" : "0px 2px 8px 0px rgba(98,244,129,0.4)",
                "&:hover": {
                  filter: "brightness(110%)",
                  borderColor: isFollowing ? theme.palette.border.green : "none",
                },
                transition: "all 0.2s ease",
              }}
            >
              {isFollowing ? "Following" : "Join"}
            </Button>
          </Box>

          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: theme.palette.text.primary, mb: 0.5 }}>
            c/{communityInfo.name}
          </Typography>

          {/* Stats row */}
          <Box sx={{ display: "flex", gap: 3, mt: 1.5 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: theme.palette.primary.main }}>
                  {(communityInfo.followersCount || 0).toLocaleString()}
                </Typography>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: theme.palette.success.main,
                    animation: "pulse 2s cubic-bezier(0.4,0,0.6,1) infinite",
                    "@keyframes pulse": {
                      "0%,100%": { opacity: 1 },
                      "50%": { opacity: 0.4 },
                    },
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "10px", color: theme.palette.text.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Members
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: theme.palette.primary.main }}>
                {communityInfo.createdAt
                  ? new Date(communityInfo.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                  : "—"}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: theme.palette.text.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Created
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Description */}
        {communityInfo.description && (
          <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${theme.palette.border.divider}`, pt: 1.5 }}>
            <Typography sx={{ fontSize: "0.875rem", color: theme.palette.text.secondary, lineHeight: 1.6 }}>
              {communityInfo.description}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create Post CTA */}
      <Button
        onClick={() => navigate("/createPost")}
        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
        fullWidth
        sx={{
          background: "linear-gradient(to right, #10b981, #059669)",
          color: "#fff",
          borderRadius: "8px",
          py: 1.25,
          fontWeight: 600,
          fontSize: "0.875rem",
          textTransform: "none",
          boxShadow: "0px 2px 8px 0px rgba(98,244,129,0.4)",
          mb: 2,
          "&:hover": { filter: "brightness(110%)" },
        }}
      >
        Create Post
      </Button>

      {/* Rules */}
      {rulesLines.length > 0 && (
        <CollapsibleSection icon={RulesIcon} title="Community Rules" defaultOpen>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {rulesLines.map((rule, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    borderRadius: "50%",
                    background: "rgba(98,244,129,0.2)",
                    border: `1px solid rgba(98,244,129,0.3)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: "12px",
                  }}
                >
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary, lineHeight: 1.5, pt: 0.25 }}>
                  {rule}
                </Typography>
              </Box>
            ))}
          </Box>
        </CollapsibleSection>
      )}

      {/* Admins */}
      {communityInfo.admins?.length > 0 && (
        <CollapsibleSection icon={AdminIcon} title="Moderators">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {communityInfo.admins.map((admin) => (
              <Box
                key={admin}
                onClick={() => navigate(`/profile/${admin}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  "&:hover": { background: theme.palette.background.paper },
                  transition: "background 0.2s ease",
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: `hsl(${(admin.charCodeAt(0) * 20) % 360}, 50%, 40%)`,
                    fontSize: "0.8rem",
                    fontWeight: 700,
                  }}
                >
                  {admin.charAt(0).toUpperCase()}
                </Avatar>
                <Typography sx={{ fontSize: "0.85rem", color: theme.palette.primary.main, fontWeight: 500 }}>
                  u/{admin}
                </Typography>
              </Box>
            ))}
          </Box>
        </CollapsibleSection>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const CommunityPage = () => {
  const { isLoggedIn, user, isAuthLoaded } = useContext(AuthContext);
  const { theme } = useTheme();
  const { communityName } = useParams();
  const [communityInfo, setCommunityInfo] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [posts, setPosts] = useState([]);
  const { currentPage, setCurrentPage } = useContext(PageContext);
  const { isMobile } = useContext(DeviceContext);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);
  const navigate = useNavigate();
  const [bannerUrl, setBannerUrl] = useState(null);
  const [iconUrl, setIconUrl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const updatePosts = (postId, upvotes, downvotes) => {
    setPosts((prev) =>
      prev.map((p) => (p.postId === postId ? { ...p, upvotes, downvotes } : p))
    );
  };

  const handleFollowToggle = async () => {
    if (!isLoggedIn) {
      alert("Please login to follow the community.");
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/community/toggleFollow/${communityInfo.id}`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setIsFollowing(response.data.follows === true || response.data.follows === "true");
    } catch (error) {
      console.error("Error toggling follow status:", error);
    }
  };

  const fetchCommunityInfo = useCallback(async () => {
    try {
      const headers =
        isLoggedIn && user
          ? { accept: "*/*", Authorization: `Bearer ${user.token}`, "Content-Type": "application/json" }
          : {};
      const response = await axios.get(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/community/info?communityName=${communityName}`,
        { headers }
      );
      setCommunityInfo(response.data);
      setIsFollowing(response.data.follows === true || response.data.follows === "true");
      setBannerUrl(response.data.banner ? getImageUrl(response.data.banner) : null);
      setIconUrl(response.data.icon ? getImageUrl(response.data.icon) : null);
    } catch (error) {
      console.error("Error fetching community info:", error);
    }
  }, [communityName, isLoggedIn, user]);

  const fetchPostsByCommunity = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/posts/byCommunityOrTags`,
        {
          params: {
            communityName,
            tags: communityName,
            sortBy: currentPage,
            page,
            size: 20,
          },
        }
      );
      const data = response.data;
      if (data?.content) {
        setPosts((prev) => {
          const newPosts = data.content.filter((p) => !prev.some((ep) => ep.postId === p.postId));
          return [...prev, ...newPosts];
        });
        setHasMore(!data.last);
      }
    } catch (error) {
      console.error("Error fetching posts by community:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [communityName, currentPage, page, hasMore, isMobile]);

  useEffect(() => {
    if (!isAuthLoaded) return;
    fetchCommunityInfo();
    setPosts([]);
    setPage(0);
    setHasMore(true);
  }, [communityName, isAuthLoaded]);

  useEffect(() => {
    if (!isAuthLoaded) return;
    fetchPostsByCommunity();
  }, [page, fetchPostsByCommunity, currentPage, isAuthLoaded]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight * 0.75 &&
        !loadingRef.current &&
        hasMore
      ) {
        setPage((prev) => prev + 1);
      }
    }, 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore]);

  const filterTabs = [
    { label: "Hot", key: "Hot", icon: HotIcon },
    { label: "Trending", key: "Trending", icon: TrendingIcon },
    { label: "New", key: "New", icon: NewIcon },
  ];

  const FilterTabsRow = () => (
    <Box sx={{ display: "flex", gap: 1, mb: 2, overflowX: "auto", pb: 0.5, "&::-webkit-scrollbar": { display: "none" } }}>
      {filterTabs.map((tab) => (
        <FilterTab
          key={tab.key}
          label={tab.label}
          icon={tab.icon}
          active={currentPage === tab.key}
          onClick={() => { setCurrentPage(tab.key); setPosts([]); setPage(0); setHasMore(true); }}
        />
      ))}
    </Box>
  );

  const postsFeed = (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {posts.map((post) => (
        <PostCommunity key={post.postId} post={post} updatePosts={updatePosts} />
      ))}
    </Box>
  );

  // ── Mobile community header (banner + icon + follow, no tabs) ──────────────
  const MobileCommunityHeader = () => (
    <Box sx={{ background: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Box
        sx={{
          height: 72,
          background: bannerUrl
            ? `url(${bannerUrl}) center/cover no-repeat`
            : `linear-gradient(135deg, rgba(16,56,32,0.5) 0%, rgba(6,30,20,0.5) 100%)`,
          position: "relative",
          "&::after": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(to top, #1a1f26 0%, transparent 70%)" },
        }}
      />
      <Box sx={{ px: 2, pb: 2, mt: "-20px", position: "relative", zIndex: 1, display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5 }}>
          <Avatar
            alt={communityInfo?.name}
            src={iconUrl || undefined}
            sx={{ width: 48, height: 48, borderRadius: "10px", border: `3px solid ${theme.palette.background.paper}`, boxShadow: "0px 4px 12px 0px rgba(98,244,129,0.3)", background: "linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)" }}
            {...(!iconUrl && communityInfo && Utils.stringAvatar(communityInfo.name))}
          />
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: theme.palette.text.primary, pb: 0.5 }}>
            c/{communityInfo?.name}
          </Typography>
        </Box>
        <Button
          onClick={handleFollowToggle}
          sx={{
            background: isFollowing ? "transparent" : "linear-gradient(to right, #10b981, #059669)",
            border: isFollowing ? `1px solid rgba(255,255,255,0.15)` : "none",
            color: isFollowing ? theme.palette.text.secondary : "#fff",
            borderRadius: "8px", px: 2, py: 0.75,
            fontWeight: 600, fontSize: "0.8rem", textTransform: "none",
            boxShadow: isFollowing ? "none" : "0px 2px 8px rgba(98,244,129,0.4)",
            "&:hover": { filter: "brightness(110%)" },
          }}
        >
          {isFollowing ? "Following" : "Join"}
        </Button>
      </Box>
    </Box>
  );

  // ── Mobile widget list — "About" community panel ────────────────────────────
  const mobileWidgets = communityInfo
    ? [
        {
          id: "about",
          label: "About",
          icon: AboutIcon,
          activeColor: theme.palette.primary.light,
          activeBg: "rgba(0,201,80,0.12)",
          activeBorder: "rgba(0,201,80,0.35)",
          render: () => (
            <Box sx={{ overflowY: "auto", maxHeight: "70vh" }}>
              <CommunityInfoPanel
                communityInfo={communityInfo}
                bannerUrl={bannerUrl}
                iconUrl={iconUrl}
                isFollowing={isFollowing}
                onFollowToggle={handleFollowToggle}
                isMobile
                navigate={navigate}
              />
            </Box>
          ),
        },
      ]
    : [];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
      <ResponsiveTopBar
        isMobile={isMobile}
        onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)}
      />

      {isMobile ? (
        // ════════════════════════════════════════════════════════════════════
        // MOBILE LAYOUT — flat feed, "About" in widget drawer
        // ════════════════════════════════════════════════════════════════════
        <Box sx={{ pt: `${TOPBAR_HEIGHT}px`, pb: "6rem" }}>
          <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
          <MobileCommunityHeader />
          <Box sx={{ px: 2, py: 2 }}>
            <FilterTabsRow />
            {postsFeed}
          </Box>
          {mobileWidgets.length > 0 && (
            <MobileWidgetDrawer widgets={mobileWidgets} />
          )}
        </Box>
      ) : (
        // ════════════════════════════════════════════════════════════════════
        // DESKTOP LAYOUT — fixed sidebars
        // ════════════════════════════════════════════════════════════════════
        <Box sx={{ display: "flex", pt: `${TOPBAR_HEIGHT}px` }}>
          {/* Left sidebar — trending posts & contributors */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              left: 0,
              bottom: 0,
              width: SIDEBAR_LEFT_WIDTH,
              backgroundColor: theme.palette.background.default,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(98,244,129,0.2)", borderRadius: 2 },
              zIndex: 100,
            }}
          >
            <Box sx={{ pt: 2, px: 1.5 }}>
              <CommunityLeftSidebar communityName={communityName} communityId={communityInfo?.id} />
            </Box>
          </Box>

          {/* Center — posts feed */}
          <Box
            sx={{
              ml: { xs: 0, lg: `${SIDEBAR_LEFT_WIDTH}px` },
              mr: { xs: 0, lg: `${SIDEBAR_RIGHT_WIDTH}px` },
              flex: 1,
              minWidth: 0,
              py: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 760, px: { md: 3, lg: 4 } }}>
              <FilterTabsRow />
              {postsFeed}
            </Box>
          </Box>

          {/* Right sidebar — community info */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              right: 0,
              bottom: 0,
              width: SIDEBAR_RIGHT_WIDTH,
              backgroundColor: theme.palette.background.default,
              borderLeft: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(98,244,129,0.2)", borderRadius: 2 },
              zIndex: 100,
            }}
          >
            <CommunityInfoPanel
              communityInfo={communityInfo}
              bannerUrl={bannerUrl}
              iconUrl={iconUrl}
              isFollowing={isFollowing}
              onFollowToggle={handleFollowToggle}
              isMobile={false}
              navigate={navigate}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CommunityPage;
